import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { IconPlus, IconTrash } from '../components/Icons';

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form State
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState('flat'); // 'flat' or 'percent'
  const [discountValue, setDiscountValue] = useState('');
  const [minOrder, setMinOrder] = useState('100');
  const [expiryDate, setExpiryDate] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'coupons'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setCoupons(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleAddCoupon = async (e) => {
    e.preventDefault();
    const formattedCode = code.trim().toUpperCase();
    if (!formattedCode || !discountValue || parseFloat(discountValue) <= 0) {
      alert('कृपया कूपन कोड आणि डिस्काउंट मूल्य टाका.');
      return;
    }

    try {
      setSubmitting(true);
      await addDoc(collection(db, 'coupons'), {
        code: formattedCode,
        discountType,
        discountValue: parseFloat(discountValue),
        minOrder: parseFloat(minOrder) || 0,
        expiryDate: expiryDate || null,
        description: description.trim() || `${formattedCode} डिस्काउंट कूपन`,
        active: true,
        usageCount: 0,
        createdAt: serverTimestamp(),
      });

      setCode('');
      setDiscountValue('');
      setDescription('');
      setExpiryDate('');
      setSubmitting(false);
      alert('कूपन कोड यशस्वीरीत्या तयार केला!');
    } catch (err) {
      setSubmitting(false);
      alert('Error: ' + err.message);
    }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      await updateDoc(doc(db, 'coupons', id), { active: !currentStatus });
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('हा कूपन कोड हटवायचा आहे का?')) {
      try {
        await deleteDoc(doc(db, 'coupons', id));
      } catch (err) {
        alert('Error: ' + err.message);
      }
    }
  };

  return (
    <div style={{ paddingBottom: 40 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', margin: '0 0 6px 0' }}>
          🎟️ Coupon Codes & Offers Management (कूपन कोड आणि सवलत)
        </h2>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          कस्टमर्ससाठी डिस्काउंट कूपन कोड्स (उदा. SANGOLA50, FIRST100) तयार करा आणि ऑन-ऑफ करा.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20 }}>
        {/* Create Coupon Form */}
        <div className="card" style={{ height: 'fit-content' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconPlus size={18} /> नवीन कूपन कोड बनवा
          </h3>

          <form onSubmit={handleAddCoupon} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>कूपन कोड (Coupon Code)</label>
              <input
                type="text"
                placeholder="e.g. SANGOLA50"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', fontWeight: 800, letterSpacing: 1 }}
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>प्रकार (Type)</label>
                <select
                  value={discountType}
                  onChange={e => setDiscountType(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                >
                  <option value="flat">Flat ₹ (फिक्स सूट)</option>
                  <option value="percent">Percentage % (टक्के)</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>डिस्काउंट (Value)</label>
                <input
                  type="number"
                  placeholder={discountType === 'flat' ? '₹50' : '10%'}
                  value={discountValue}
                  onChange={e => setDiscountValue(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                  required
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>किमान ऑर्डर मूल्य (Min Order ₹)</label>
              <input
                type="number"
                placeholder="e.g. 200"
                value={minOrder}
                onChange={e => setMinOrder(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>अंतिम तारीख (Expiry Date - Optional)</label>
              <input
                type="date"
                value={expiryDate}
                onChange={e => setExpiryDate(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>वर्णन / माहिती (Description)</label>
              <input
                type="text"
                placeholder="e.g. ₹50 Off on orders above ₹200"
                value={description}
                onChange={e => setDescription(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={submitting} style={{ marginTop: 4 }}>
              {submitting ? 'तयार होत आहे...' : '🎟️ कूपन सेव्ह करा (Create Coupon)'}
            </button>
          </form>
        </div>

        {/* Coupons List */}
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>सध्याचे कूपन कोड्स (Active & Past Coupons)</h3>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>लोड होत आहे...</div>
          ) : coupons.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>कोणताही कूपन कोड तयार केलेला नाही.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
              {coupons.map(c => (
                <div
                  key={c.id}
                  style={{
                    border: '1.5px dashed',
                    borderColor: c.active ? 'var(--primary)' : 'var(--border)',
                    borderRadius: 12, padding: 14,
                    background: c.active ? 'rgba(255, 107, 53, 0.04)' : 'var(--bg)',
                    position: 'relative'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--primary)', letterSpacing: 1 }}>
                      🎟️ {c.code}
                    </div>
                    <span className={`badge ${c.active ? 'success' : 'danger'}`}>
                      {c.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                    {c.discountType === 'flat' ? `₹${c.discountValue} OFF` : `${c.discountValue}% OFF`}
                  </div>

                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                    {c.description} <br />
                    • Min Order: ₹{c.minOrder || 0} <br />
                    {c.expiryDate && <>• Exp: {c.expiryDate}</>}
                  </div>

                  <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                    <button
                      onClick={() => toggleActive(c.id, c.active)}
                      className={`btn ${c.active ? 'btn-secondary' : 'btn-success'} btn-sm`}
                      style={{ flex: 1, fontSize: 11, justifyContent: 'center' }}
                    >
                      {c.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="btn btn-danger btn-sm"
                      style={{ padding: '4px 8px' }}
                    >
                      <IconTrash size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
